import React, { useState } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  margin-bottom: 8px;
  max-width: 300px;
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background-color: #f0f2f5;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: auto;
    max-height: 200px;
    object-fit: cover;
    display: block;
  }
`;

const Caption = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #111b21;
  line-height: 1.3;
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f0f2f5;
  border-radius: 8px;
  color: #667781;
  min-height: 100px;
  flex-direction: column;
`;

const ErrorPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #f0f2f5;
  border-radius: 8px;
  color: #667781;
  cursor: pointer;
  min-height: 100px;
  
  &:hover {
    background-color: #e9edef;
  }
`;

const ImageMessage = ({ message, brandCode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);


  const API_URL2 = import.meta.env.VITE_API_URL2;

  const getImageUrl = () => {
    if (message.imageUrl) {
      return message.imageUrl;
    }
    
    if (message.imageId) {
      const token = localStorage.getItem('brandToken') || 
                   localStorage.getItem('token') || 
                   localStorage.getItem('auth_token');
      
      const baseUrl = API_URL2 + `/api/images/download/${message.imageId}`;
      const params = new URLSearchParams({
        brandCode: brandCode
      });
      
      if (token) {
        params.append('token', token);
      }
      
      return `${baseUrl}?${params.toString()}`;
    }
    
    return null;
  };

  const handleImageClick = () => {
    const url = getImageUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleImageLoad = () => {
    // console.log('[IMAGE] Image loaded successfully');
    setLoading(false);
    setError(false);
  };

  const handleImageError = (e) => {
    // console.error('[IMAGE] Failed to load image:', e);
    setLoading(false);
    setError(true);
  };

  const handleRetry = () => {
    // console.log('[IMAGE] Retrying image load...');
    setError(false);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };

  // Debug logging
  React.useEffect(() => {
    // console.log('[IMAGE] Component props:', {
    //   messageType: message.messageType,
    //   imageId: message.imageId,
    //   imageUrl: message.imageUrl,
    //   brandCode: brandCode,
    //   generatedUrl: getImageUrl()
    // });
  }, [message, brandCode]);

  // Check if this is actually an image message
  if (message.messageType !== 'image' && !message.imageId && !message.imageUrl) {
    // console.log('[IMAGE] Not an image message, skipping render');
    return null;
  }

  const imageUrl = getImageUrl();
  if (!imageUrl) {
    // console.log('[IMAGE] No image URL available');
    return (
      <ErrorPlaceholder>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
        <div style={{ fontSize: '14px', textAlign: 'center' }}>
          No image URL available
        </div>
      </ErrorPlaceholder>
    );
  }

  return (
    <ImageContainer>
      <ImageWrapper onClick={handleImageClick}>
        {loading && !error && (
          <LoadingPlaceholder>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #00a884',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '8px'
            }} />
            Loading image...
          </LoadingPlaceholder>
        )}
        
        {error ? (
          <ErrorPlaceholder onClick={handleRetry}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
            <div style={{ fontSize: '14px', textAlign: 'center' }}>
              Image unavailable
              <br />
              <span style={{ fontSize: '12px' }}>Click to try again</span>
            </div>
            {retryCount > 0 && (
              <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>
                Retry #{retryCount}
              </div>
            )}
          </ErrorPlaceholder>
        ) : (
          <img
            key={`${imageUrl}-${retryCount}`} // Force re-render on retry
            src={imageUrl}
            alt={message.caption || 'Image'}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: loading ? 'none' : 'block' }}
          />
        )}
      </ImageWrapper>
      
      {message.caption && (
        <Caption>{message.caption}</Caption>
      )}
    </ImageContainer>
  );
};

// Add CSS for loading animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default ImageMessage;